import {
  TextInput,
  Stack,
  Switch,
  Select,
  MultiSelect,
  NumberInput,
  SimpleGrid,
  Tooltip,
  ActionIcon,
  Group,
  Divider,
} from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { useForm } from "@mantine/form";
import { type ChangeEvent, type Ref, useMemo, useCallback } from "react";
import { User } from "../../../services/admin/users.service";
import { useRolesAdmin } from "../../../hooks/admin/useRoles";
import { useFacilitiesAdmin } from "../../../hooks/admin/useFacilities";
import { useGroupsAdmin } from "../../../hooks/admin/useGroups";
import { required, scrollToFirstError } from "../../../utils/formValidation";
import { SignatureUpload } from "./SignatureUpload";

export interface UserFormValues {
  username: string;
  email: string;
  full_name: string;
  credentials: string;
  role_id: string;
  facility_id: string;
  groupIds: string[];
  pathologist_maximum_daily_cases: number | string;
  is_active: boolean;
  is_default_qa_reviewer: boolean;
  signatureImageUrl: string | null;
  is_remote: boolean;
  no_shipping: boolean;
  remote_address_street: string;
  remote_address_city: string;
  remote_address_state: string;
  remote_address_zip: string;
}

const getInitialGroupIds = (user?: User): string[] => {
  if (!user?.userGroups) return [];
  return user.userGroups
    .map((ug) => ug.group?.id ?? ug.groupId)
    .filter(Boolean);
};

interface UserFormProps {
  initialValues?: User;
  onSubmit: (values: UserFormValues) => void;
  isLoading?: boolean;
  formRef?: Ref<HTMLFormElement>;
}

export default function UserForm({
  initialValues,
  onSubmit,
  isLoading,
  formRef,
}: UserFormProps) {
  const { data: roles = [] } = useRolesAdmin();
  const { data: facilities = [] } = useFacilitiesAdmin();
  const { data: groups = [] } = useGroupsAdmin();

  const form = useForm<UserFormValues>({
    validateInputOnBlur: true,
    initialValues: {
      username: initialValues?.username || "",
      email: initialValues?.email || "",
      full_name: initialValues?.full_name || "",
      credentials: initialValues?.credentials || "",
      role_id: initialValues?.role_id || "",
      facility_id: initialValues?.facility_id || "",
      groupIds: getInitialGroupIds(initialValues),
      pathologist_maximum_daily_cases:
        initialValues?.pathologist_maximum_daily_cases || "",
      is_active: initialValues?.is_active ?? true,
      is_default_qa_reviewer: initialValues?.is_default_qa_reviewer ?? false,
      signatureImageUrl: initialValues?.signatureImageUrl || null,
      is_remote: initialValues?.is_remote ?? false,
      no_shipping: initialValues?.no_shipping ?? false,
      remote_address_street: initialValues?.remote_address_street || "",
      remote_address_city: initialValues?.remote_address_city || "",
      remote_address_state: initialValues?.remote_address_state || "",
      remote_address_zip: initialValues?.remote_address_zip || "",
    },
    validate: {
      username: required("Username is required"),
      email: (value) => {
        if (!value) return "Email is required";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
          return "Invalid email address";
        return null;
      },
      full_name: required("Full name is required"),
      role_id: required("Role is required"),
      remote_address_street: (value, values) =>
        values.is_remote && !values.no_shipping && !value
          ? "Street address is required"
          : null,
      remote_address_city: (value, values) =>
        values.is_remote && !values.no_shipping && !value
          ? "City is required"
          : null,
      remote_address_state: (value, values) =>
        values.is_remote && !values.no_shipping && !value
          ? "State is required"
          : null,
      remote_address_zip: (value, values) =>
        values.is_remote && !values.no_shipping && !value
          ? "ZIP code is required"
          : null,
    },
  });

  const handleSubmit = useMemo(
    () =>
      form.onSubmit(
        (values) => onSubmit(values),
        () => scrollToFirstError(form)
      ),
    [form, onSubmit]
  );

  const roleOptions = useMemo(
    () =>
      roles
        .filter((r) => r.isActive)
        .map((r) => ({ value: r.id, label: r.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [roles]
  );

  const facilityOptions = useMemo(
    () =>
      facilities
        .filter((f) => f.is_active)
        .map((f) => ({ value: f.id, label: f.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [facilities]
  );

  const groupOptions = useMemo(
    () =>
      groups
        .map(
          (g: {
            id: string;
            name: string;
            is_active?: boolean;
            isActive?: boolean;
          }) => ({
            value: g.id,
            label: g.name,
          })
        )
        .sort((a, b) => a.label.localeCompare(b.label)),
    [groups]
  );

  const handleSignatureChange = useCallback(
    (key: string | null) => {
      form.setFieldValue("signatureImageUrl", key);
    },
    [form]
  );

  const handleRemoteChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const isRemote = event.currentTarget.checked;
      form.setFieldValue("is_remote", isRemote);
      if (!isRemote) form.setFieldValue("no_shipping", false);
    },
    [form]
  );

  return (
    <form ref={formRef} noValidate onSubmit={handleSubmit}>
      <Stack gap="md">
        <SimpleGrid cols={2}>
          <TextInput
            label="Full Name"
            placeholder="Enter full name"
            withAsterisk
            disabled={isLoading}
            {...form.getInputProps("full_name")}
          />
          <TextInput
            label="Email"
            placeholder="Enter email address"
            type="email"
            withAsterisk
            disabled={isLoading}
            {...form.getInputProps("email")}
          />
        </SimpleGrid>

        <SimpleGrid cols={2}>
          <TextInput
            label="Username"
            placeholder="Enter username"
            withAsterisk
            disabled={isLoading}
            {...form.getInputProps("username")}
          />
          <Select
            label="Role"
            placeholder="Select role"
            data={roleOptions}
            searchable
            clearable
            withAsterisk
            disabled={isLoading}
            {...form.getInputProps("role_id")}
          />
        </SimpleGrid>

        <SimpleGrid cols={2}>
          <TextInput
            label={
              <Group gap={4} align="center">
                <span>Credentials</span>
                <Tooltip label="Displayed on reports" withArrow position="top">
                  <ActionIcon
                    variant="transparent"
                    size="xs"
                    color="gray"
                    tabIndex={-1}
                  >
                    <IconInfoCircle size={14} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            }
            placeholder="e.g. M.D., D.O."
            disabled={isLoading}
            {...form.getInputProps("credentials")}
          />
          <Select
            label="Facility"
            placeholder="Select facility"
            data={facilityOptions}
            searchable
            clearable
            disabled={isLoading}
            {...form.getInputProps("facility_id")}
          />
        </SimpleGrid>

        <MultiSelect
          label="Groups"
          placeholder="Select groups"
          description="Assign this user to one or more groups"
          data={groupOptions}
          searchable
          clearable
          disabled={isLoading}
          {...form.getInputProps("groupIds")}
        />

        <SimpleGrid cols={2}>
          <NumberInput
            label="Max Daily Cases"
            placeholder="Enter maximum daily cases"
            min={0}
            disabled={isLoading}
            {...form.getInputProps("pathologist_maximum_daily_cases")}
          />
        </SimpleGrid>

        <Switch
          label="Active"
          disabled={isLoading}
          {...form.getInputProps("is_active", { type: "checkbox" })}
        />

        <Switch
          label="Default QA Reviewer"
          description="Set this user as the default QA reviewer for external opinion send-outs. Only one user can be the default at a time."
          disabled={isLoading}
          color="violet"
          {...form.getInputProps("is_default_qa_reviewer", {
            type: "checkbox",
          })}
        />

        <Divider my="xs" label="Remote Shipping Address" labelPosition="left" />

        <Stack gap="xs">
          <Switch
            label="Remote Pathologist"
            description="If enabled, cases assigned to this pathologist will be routed to their remote address for shipping."
            disabled={isLoading}
            color="blue"
            checked={form.values.is_remote}
            onChange={handleRemoteChange}
          />

          {form.values.is_remote && (
            <Stack gap="sm">
              <Switch
                label="No shipping"
                description="Route assigned cases directly to pathologist review without physical shipping."
                disabled={isLoading}
                color="blue"
                {...form.getInputProps("no_shipping", {
                  type: "checkbox",
                })}
              />

              {!form.values.no_shipping && (
                <Stack gap="sm">
                  <TextInput
                    label="Remote Shipping Street"
                    placeholder="Enter street address"
                    disabled={isLoading}
                    {...form.getInputProps("remote_address_street")}
                  />
                  <SimpleGrid cols={3}>
                    <TextInput
                      label="City"
                      placeholder="City"
                      disabled={isLoading}
                      {...form.getInputProps("remote_address_city")}
                    />
                    <TextInput
                      label="State"
                      placeholder="State"
                      disabled={isLoading}
                      {...form.getInputProps("remote_address_state")}
                    />
                    <TextInput
                      label="ZIP Code"
                      placeholder="ZIP"
                      disabled={isLoading}
                      {...form.getInputProps("remote_address_zip")}
                    />
                  </SimpleGrid>
                </Stack>
              )}
            </Stack>
          )}
        </Stack>

        <Divider my="xs" />

        <SignatureUpload
          currentSignatureUrl={initialValues?.signatureImagePreviewUrl}
          hasExistingSignature={Boolean(form.values.signatureImageUrl)}
          onSignatureChange={handleSignatureChange}
        />
      </Stack>
    </form>
  );
}

export { useForm } from "@mantine/form";
